// Staff sign-in, shared by every staff site.
//
// Staff sign in once, at the start page (staff.silverservice.com). A site with
// no sign-in of its own sends the browser there; the start page sends it back
// with a one-time code that the site trades for its own copy of the sign-in
// (backend/lib/staffHandoff.js has the server side). The token never travels
// in a URL, and nothing is shared through a cookie that www or orders could
// read.
import { parseHash, startPageUrl } from './sites';

// Where each site keeps its copy of the sign-in. In prod every site is its own
// subdomain, so these never meet. On staging every site shares one origin
// (picked by #hash) and the separate keys keep them apart the way prod is —
// except Manage, Purchasing and Reports, which share the /admin/* API and so
// one key.
const TOKEN_KEYS = {
  staff: 'ss_staff_token',
  manage: 'ss_admin_token',
  purchase: 'ss_admin_token',
  reports: 'ss_admin_token',
  sales: 'ss_sales_token',
  service: 'ss_service_token',
  fulfillment: 'ss_fulfillment_token',
  receipts: 'ss_capture_token',
  // Ordering's narrow staff sign-in, for the customer preview only. The
  // preview itself is a customer sign-in and lives where customers' do.
  orders: 'ss_orders_staff_token',
};

export const STAFF_SIGNED_OUT = 'ss-staff-signed-out';

export const staffTokenKey = (site) => TOKEN_KEYS[site];

export function getStaffToken(site) {
  try { return localStorage.getItem(TOKEN_KEYS[site]) || ''; } catch { return ''; }
}

export function setStaffToken(site, token) {
  localStorage.setItem(TOKEN_KEYS[site], token);
}

export function clearStaffToken(site) {
  try { localStorage.removeItem(TOKEN_KEYS[site]); } catch { /* nothing stored */ }
}

// The API refused this site's sign-in (expired, signed out elsewhere, account
// turned off). useStaffSession hears this and sends the browser to sign in.
export function staffSignedOut(site) {
  clearStaffToken(site);
  window.dispatchEvent(new CustomEvent(STAFF_SIGNED_OUT, { detail: { key: TOKEN_KEYS[site] } }));
}

// ── The round trip to the start page ──
// `state` is a random value this tab remembers and the start page hands back
// with the code. A code arriving without it — say, in a link someone sent — is
// ignored, so nobody can sign you in as them.
const stateKey = (site) => `ss_handoff_state:${site}`;
const bouncesKey = (site) => `ss_handoff_bounces:${site}`;
const MAX_BOUNCES_PER_MINUTE = 3;

function randomState() {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

// The start-page URL that signs this site in, or null when this tab has been
// round several times in the last minute — something keeps refusing the
// sign-in, and looping would only hide it.
export function signInUrlFor(site) {
  try {
    const now = Date.now();
    const recent = JSON.parse(sessionStorage.getItem(bouncesKey(site)) || '[]').filter((t) => now - t < 60000);
    if (recent.length >= MAX_BOUNCES_PER_MINUTE) return null;
    sessionStorage.setItem(bouncesKey(site), JSON.stringify([...recent, now]));
    const state = randomState();
    sessionStorage.setItem(stateKey(site), state);
    return startPageUrl({ return: site, state });
  } catch {
    return null;
  }
}

export function forgetBounces(site) {
  try { sessionStorage.removeItem(bouncesKey(site)); } catch { /* nothing stored */ }
}

// The code the start page sent back, if it is the one this tab asked for. The
// code leaves the address bar either way.
export function takeHandoffCode(site) {
  const { page, params } = parseHash();
  const code = params.get('handoff');
  if (!code) return null;
  window.history.replaceState(null, '', window.location.pathname + window.location.search + (page ? `#${page}` : ''));
  let expected = null;
  try {
    expected = sessionStorage.getItem(stateKey(site));
    sessionStorage.removeItem(stateKey(site));
  } catch { /* no state, so the code is refused below */ }
  return expected && params.get('state') === expected ? code : null;
}
